import { useCallback, useEffect, useRef, useState } from "react";
import CheckBox from "@avinet/adaptive-ui-core/form/controls/CheckBox";
import FormProvider from "@avinet/adaptive-ui-core/form/FormProvider";
import useForm, { useFormApi } from "@avinet/adaptive-ui-core/form/useForm";
import Grow from "@avinet/adaptive-ui-core/layout/Grow";
import Row from "@avinet/adaptive-ui-core/layout/Row";
import Dialog from "@avinet/adaptive-ui-core/ui/Dialog";
import { useTranslate } from "@avinet/adaptive-ui-translate";
import useSessionToken from "../../hooks/useSessionToken";
import { InputSection, InputSectionType } from "../../types/Section";
import { patchRecord } from "../../utils/api";
import InputSectionRenderer from "../form/InputSectionRenderer";
import { Icon } from "../Icon";
import { LoadingMask } from "../Loading";
import { formatValue } from "./formatValue";

const Toggle = ({
  value,
  onToggle,
}: {
  value?: boolean;
  onToggle: (value: boolean) => void;
}) => {
  const form = useFormApi();
  const v = form.getValue("value");

  useEffect(() => {
    if (v === value) return;
    onToggle(v);
  }, [v, value]);

  return <CheckBox id="value" label={null} />;
};

const EditableCell = ({
  uuid,
  section,
  value,
  record,
}: {
  uuid: string;
  section: InputSection;
  value: any;
  record: any;
}) => {
  const t = useTranslate("Results");
  const [editing, setEditing] = useState(false);

  const savedValue = useRef(value);
  const [saving, setSaving] = useState(false);
  const sessionToken = useSessionToken();
  const id = record.id;

  const onSave = useCallback(
    ({ value }) => {
      if (!sessionToken) return;

      if (value === savedValue.current) {
        setEditing(false);
        return;
      }

      setSaving(true);

      patchRecord(uuid, id, { [section.columnName]: value }, sessionToken)
        .then(() => {
          savedValue.current = value;
          setSaving(false);
          setEditing(false);
        })
        .catch((err) => {
          console.warn(err);

          if (section.type === InputSectionType.boolean)
            form.setValue("value", savedValue.current);

          setSaving(false);
        });
    },
    [sessionToken, uuid, id, section]
  );

  const { state, form } = useForm({
    defaultValues: { value },
    onSubmit: onSave,
  });

  switch (section.type) {
    case InputSectionType.boolean:
      return (
        <>
          <FormProvider form={form} state={state}>
            <Toggle
              value={savedValue.current}
              onToggle={(value: boolean) => onSave({ value })}
            />
            {saving && <LoadingMask />}
          </FormProvider>
        </>
      );
  }

  return (
    <Row>
      <Grow>
        {savedValue.current
          ? formatValue(section, savedValue.current, t, sessionToken)
          : "-"}
      </Grow>
      <button
        type="button"
        className="btn-link icon small"
        title={t("editValue", { label: section.label })}
        onClick={() => setEditing(true)}
      >
        <Icon name="edit" />
      </button>
      {editing && (
        <Dialog title={t("editValue", { label: section.label })}>
          <FormProvider form={form} state={state}>
            <form onSubmit={form.submit}>
              <InputSectionRenderer id="value" section={section} />
              <Row>
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => setEditing(false)}
                >
                  {t("cancel")}
                </button>
                <Grow />
                <button type="submit" className="btn">
                  <Icon name="checkmark" /> {t("save")}
                </button>
              </Row>
              {saving && <LoadingMask />}
            </form>
          </FormProvider>
        </Dialog>
      )}
    </Row>
  );
};

export default EditableCell;
